import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

// ref_07 grammar: filter pills with real counts → toolbar → dense table → tinted footer banner.
export function ViewTabs({ tabs, value, onChange, counts = {}, testId }) {
  return (
    <div role="tablist" data-testid={testId} className="flex flex-wrap gap-2">
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button key={t.value} type="button" role="tab" aria-selected={active} data-testid={`${testId}-${t.value.toLowerCase()}`} onClick={() => onChange(t.value)}
            className={cn("inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-colors", active ? "border-[#BFD3F5] bg-[#E8F0FC] text-[#1F4E9C]" : "border-jigsaw-border bg-white text-jigsaw-navy hover:bg-jigsaw-bg")}>
            {t.label}
            {counts[t.value] !== undefined && <span className={cn("text-[12px]", active ? "text-[#1F4E9C]/70" : "text-jigsaw-muted")}>({counts[t.value]})</span>}
          </button>
        );
      })}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, testId, className }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  useEffect(() => {
    if (local === (value || "")) return undefined;
    const t = setTimeout(() => onChange(local), 300);
    return () => clearTimeout(t);
  }, [local]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Input data-testid={testId} value={local} onChange={(e) => setLocal(e.target.value)} placeholder={placeholder} className={cn("h-9 w-full sm:max-w-[260px]", className)} />;
}

export function InfoBanner({ icon: Icon, title, text, actionLabel, to, onAction, testId }) {
  const action = to
    ? <Link to={to} data-testid={`${testId}-action`} className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-jigsaw-navy px-3.5 text-[13px] font-medium text-white hover:bg-jigsaw-navy/90">{actionLabel} <ArrowRight className="h-3.5 w-3.5" /></Link>
    : <button type="button" onClick={onAction} data-testid={`${testId}-action`} className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-jigsaw-navy px-3.5 text-[13px] font-medium text-white hover:bg-jigsaw-navy/90">{actionLabel} <ArrowRight className="h-3.5 w-3.5" /></button>;
  return (
    <div data-testid={testId} className="flex flex-col gap-3 rounded-[10px] border border-jigsaw-navy/10 bg-[#EEF3FB] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {Icon && <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-jigsaw-navy"><Icon className="h-4 w-4" /></span>}
        <div><p className="text-[14px] font-semibold text-jigsaw-navy">{title}</p><p className="text-[13px] text-jigsaw-muted">{text}</p></div>
      </div>
      {actionLabel && action}
    </div>
  );
}

// URL-backed list state (view/q/page) so filters survive reload and back navigation; changing a filter resets paging.
export function useListParams(defaults = { view: "ALL" }) {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || defaults.view;
  const q = params.get("q") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const update = (mutate) => { const next = new URLSearchParams(params); mutate(next); setParams(next); };
  return {
    view, q, page,
    setView: (v) => update((n) => { if (v === defaults.view) n.delete("view"); else n.set("view", v); n.delete("page"); }),
    setQ: (v) => update((n) => { if (!v) n.delete("q"); else n.set("q", v); n.delete("page"); }),
    setPage: (p) => update((n) => { if (p <= 1) n.delete("page"); else n.set("page", String(p)); }),
  };
}

export function ViewLink({ onClick, testId, label = "View" }) {
  return <button type="button" data-testid={testId} onClick={(e) => { e.stopPropagation(); onClick(); }} className="text-[13px] font-medium text-[#1F4E9C] underline-offset-2 hover:underline">{label}</button>;
}
